import { useRef, useState, type ReactElement, type RefObject } from "react";
import { Link, useLocation } from "react-router";

import type { FieldErrors } from "../../api/client.js";
import { useApiData } from "../../api/use-api-data.js";
import { usePageMeta } from "../../app/use-page-meta.js";
import { ApiState, titleFor } from "../../components/api-state.js";
import { useRowLink } from "../../components/row-link.js";

import { CenterBackLink } from "./center-back-link.js";
import { CsvDownload, csvLimitLabel } from "./csv-download.js";
import { EstimateSearchForm } from "./estimate-search-form.js";
import type { CenterEstimateRow, CenterEstimates, EstimateConditions, EstimatePlanChoice } from "./estimate-types.js";
import { Pager } from "./pager.js";
import { adminCenterApi, adminCenterUrls } from "./urls.js";

const noErrors: FieldErrors = {};

/** 断られた（400）ときに窓へ戻す条件。<b>URL のクエリのまま</b>（打った値を消さない）。 */
const conditionsOf = (search: string): EstimateConditions => {
    const query = new URLSearchParams(search);
    return {
        number: query.get("number"), addressee: query.get("addressee"), from: query.get("from"), to: query.get("to"),
        plan: query.get("plan"),
    };
};

/** 今の結果の条件（サーバが返した値。CSV とページ送りに載せる）。 */
const conditionsIn = (found: CenterEstimates): EstimateConditions => ({
    number: found.number, addressee: found.addressee, from: found.from, to: found.to, plan: found.plan,
});

function EstimateRows({ rows }: { readonly rows: readonly CenterEstimateRow[] }): ReactElement {
    const rowLink = useRowLink();

    return (
        <tbody>
            {rows.map((row) => {
                const href = adminCenterUrls.estimate(row.id);
                return (
                    // 行ごと押せる。**番号のボタンは残す**——キーボードと読み上げはそちらを辿る
                    <tr key={row.id} data-row-href={href} onClick={rowLink(href)}>
                        <td><Link to={href} className="btn-secondary btn-sm">{row.estimateNumber}</Link></td>
                        <td className="whitespace-nowrap">{row.issueDateLabel}</td>
                        <td className="whitespace-nowrap">{row.validUntilLabel}</td>
                        <td className="font-medium text-ink-800">{row.addressee}</td>
                        <td className="text-sm">{row.summaryLabel}</td>
                        <td className="cell-number whitespace-nowrap">{row.totalLabel}</td>
                        <td className="text-sm">
                            {row.createdByLabel ?? <span className="badge-muted">ログインせずに作成</span>}
                        </td>
                        <td className="whitespace-nowrap text-slate-500">{row.createdLabel}</td>
                    </tr>
                );
            })}
        </tbody>
    );
}

function Results({ found, top }: {
    readonly found: CenterEstimates;
    readonly top: RefObject<HTMLDivElement | null>;
}): ReactElement {
    if (found.rows.length === 0) {
        return <div className="empty-state text-sm text-slate-500">当たる見積書がありません。</div>;
    }
    const limit = csvLimitLabel(found.csvLimit);
    const conditions = conditionsIn(found);

    return (
        <>
            <CsvDownload
                filePrefix="estimates"
                url={adminCenterApi.estimatesCsv({ ...conditions })}
                summary={`${String(found.matchingCount)} 件`}
                help={`CSV には、ページに関係なく当たる見積書を新しい順に ${limit} 件まで書き出します。`}
                warning={found.matchingCount > found.csvLimit
                    ? `当たりが ${limit} 件を超えています。CSV には新しい順に ${limit} 件までを書き出します。期間で絞ってください。`
                    : null}
            />
            <div className="table-card" ref={top}>
                <div className="table-scroll">
                    <table className="data-table">
                        <thead>
                            <tr>
                                <th scope="col">見積番号</th>
                                <th scope="col">発行日</th>
                                <th scope="col">有効期限</th>
                                <th scope="col">宛名</th>
                                <th scope="col">内容</th>
                                <th scope="col" className="cell-number">合計（税込・月額）</th>
                                <th scope="col">作った人</th>
                                <th scope="col">作成日時</th>
                            </tr>
                        </thead>
                        <EstimateRows rows={found.rows} />
                    </table>
                </div>
            </div>
            <Pager
                page={found.page}
                pageCount={found.pageCount}
                hasNext={found.page < found.pageCount}
                total={`全 ${String(found.matchingCount)} 件`}
                hrefOf={(page) => adminCenterUrls.estimates({ ...conditions, page })}
                top={top}
                marker={{ "data-estimate-pager": "" }}
            />
        </>
    );
}

/**
 * 見積の検索（料金プランのページから作られた見積書。Implem.Portal.Backend の docs/features/37 §6）。
 * <b>条件は URL のクエリ</b>（<c>number</c>・<c>addressee</c>・<c>from</c>・<c>to</c>・<c>plan</c>・<c>page</c>）。
 * 開けるかはサーバが決める（請求の権限。無ければ 404 →「見つからない」）。何件ずつかもサーバが決める（50 件）。
 *
 * <b>番号・期間・プランの誤りは 400（ValidationProblem）で断られる。</b>そのときも窓は残し、断りを欄の下（番号・期間）か先頭に出す。
 * プランの選択肢は最後に届いたものを使う。
 */
export function EstimateSearchPage(): ReactElement | null {
    const { search } = useLocation();
    const data = useApiData<CenterEstimates>(adminCenterApi.estimates(search));
    usePageMeta(titleFor(data, "見積の検索"));
    const top = useRef<HTMLDivElement | null>(null);
    const [plans, setPlans] = useState<readonly EstimatePlanChoice[]>([]);
    if (data.state === "ready" && data.value.plans !== plans) {
        setPlans(data.value.plans);
    }

    const rejected = data.state === "failed" && data.status === 400 && data.errors !== undefined;
    if (data.state !== "ready" && !rejected) {
        return <ApiState data={data} what="見積書" />;
    }

    return (
        <div className="page max-w-6xl">
            <CenterBackLink />
            <div className="page-header">
                <div>
                    <h1 className="page-title">見積の検索</h1>
                    <p className="page-subtitle">
                        料金プランのページから作られた見積書を、番号・宛名・発行日・プランで探します。番号から見積書を開いて印刷できます。
                    </p>
                </div>
            </div>

            {/* 窓は断られたときも同じ位置に描く（作り直すとフォーカスが外れる。docs/01 §8） */}
            {data.state === "ready"
                ? <EstimateSearchForm conditions={data.value} plans={data.value.plans} errors={noErrors} />
                : <EstimateSearchForm conditions={conditionsOf(search)} plans={plans} errors={data.errors ?? noErrors} />}
            {data.state === "ready" ? <Results found={data.value} top={top} /> : null}
        </div>
    );
}
